import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Copy, ExternalLink, CheckCircle, 
  FileCode, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

// Contract verification data
const CONTRACTS = [
  {
    name: "BethUtilityToken",
    address: "0x6822824c5349bf005e353ec5dda3fa34595ea811",
    compiler: "0.8.20",
    constructorArgs: null,
    sourceFile: "BethUtilityToken_Simple.sol",
    description: "1 Trillion BETH utility token for collateral"
  },
  {
    name: "CollateralVault",
    address: "0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d",
    compiler: "0.8.20",
    constructorArgs: "0000000000000000000000006822824c5349bf005e353ec5dda3fa34595ea8110000000000000000000000002fb355b99b2060e42db50d99393d00f47a41d322",
    constructorParams: [
      { name: "_bethToken", value: "0x6822824c5349bf005e353ec5dda3fa34595ea811" },
      { name: "_feeRecipient", value: "0x2FB355B99b2060e42DB50D99393D00f47A41d322" }
    ],
    sourceFile: "CollateralVault_Simple.sol",
    description: "Secure vault for BETH collateral deposits"
  },
  {
    name: "CreditLineContract",
    address: "0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd",
    compiler: "0.8.20",
    constructorArgs: "000000000000000000000000e86c1f0fd87165cbde68714e6f4cb152e7e5707d0000000000000000000000002fb355b99b2060e42db50d99393d00f47a41d322",
    constructorParams: [
      { name: "_collateralVault", value: "0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d" },
      { name: "_treasury", value: "0x2FB355B99b2060e42DB50D99393D00f47A41d322" }
    ],
    sourceFile: "CreditLineContract_Simple.sol",
    description: "Credit line management with collateral backing"
  },
  {
    name: "LiquidationEngine",
    address: "0x65e67b63513de0b2bf463c47a2a284254e23fcda",
    compiler: "0.8.20",
    constructorArgs: "000000000000000000000000e86c1f0fd87165cbde68714e6f4cb152e7e5707d0000000000000000000000006822824c5349bf005e353ec5dda3fa34595ea8110000000000000000000000002fb355b99b2060e42db50d99393d00f47a41d322",
    constructorParams: [
      { name: "_collateralVault", value: "0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d" },
      { name: "_bethToken", value: "0x6822824c5349bf005e353ec5dda3fa34595ea811" },
      { name: "_treasury", value: "0x2FB355B99b2060e42DB50D99393D00f47A41d322" }
    ],
    sourceFile: "LiquidationEngine_Simple.sol",
    description: "Automated liquidation for underwater positions"
  },
  {
    name: "TreasuryManager",
    address: "0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c",
    compiler: "0.8.20",
    constructorArgs: "0000000000000000000000006822824c5349bf005e353ec5dda3fa34595ea8110000000000000000000000002fb355b99b2060e42db50d99393d00f47a41d3220000000000000000000000002fb355b99b2060e42db50d99393d00f47a41d3220000000000000000000000002fb355b99b2060e42db50d99393d00f47a41d322",
    constructorParams: [
      { name: "_bethToken", value: "0x6822824c5349bf005e353ec5dda3fa34595ea811" },
      { name: "_operationsFund", value: "0x2FB355B99b2060e42DB50D99393D00f47A41d322" },
      { name: "_reserveFund", value: "0x2FB355B99b2060e42DB50D99393D00f47A41d322" },
      { name: "_teamFund", value: "0x2FB355B99b2060e42DB50D99393D00f47A41d322" }
    ],
    sourceFile: "TreasuryManager_Simple.sol",
    description: "Treasury fund distribution and management"
  }
];

const ContractVerification = () => {
  const [expandedContract, setExpandedContract] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState({});

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const toggleExpand = (index) => {
    setExpandedContract(expandedContract === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="contract-verification">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-2">
            <Link to="/treasury">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Treasury
              </Button>
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-yellow-400" />
            Etherscan Contract Verification
          </h1>
          <p className="text-gray-400 text-sm">Verify smart contract source code on Etherscan</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Instructions */}
        <Card className="bg-blue-900/20 border-blue-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileCode className="w-5 h-5 text-blue-400" />
              Verification Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-gray-300">
              <li className="flex gap-3">
                <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                <span>Click "Verify on Etherscan" for the contract you want to verify</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                <span>Select <strong>Solidity (Single file)</strong> and compiler version <strong>v0.8.20+commit.a1b79de6</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                <span>Choose <strong>MIT License</strong> for Open Source License</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                <span>Paste the source code from the expandable section below</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">5</span>
                <span>If required, paste the Constructor Arguments (ABI-encoded)</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Contracts List */}
        <div className="space-y-4">
          {CONTRACTS.map((contract, index) => (
            <Card key={contract.name} className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      {contract.name}
                      {verificationStatus[contract.address] && (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      )}
                    </CardTitle>
                    <CardDescription>{contract.description}</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleExpand(index)}
                  >
                    {expandedContract === index ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-[#1a2d4a]/50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Contract Address</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-mono text-xs truncate">{contract.address}</p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(contract.address, 'Address')}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 bg-[#1a2d4a]/50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Compiler Version</p>
                    <p className="text-white font-mono text-sm">v{contract.compiler}+commit.a1b79de6</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mb-4">
                  <a 
                    href={`https://etherscan.io/address/${contract.address}#code`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button className="w-full bg-yellow-600 hover:bg-yellow-700">
                      <Shield className="w-4 h-4 mr-2" />
                      Verify on Etherscan
                    </Button>
                  </a>
                  <a 
                    href={`https://etherscan.io/address/${contract.address}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>

                {/* Expanded Details */}
                {expandedContract === index && (
                  <div className="space-y-4 pt-4 border-t border-[#1a2d4a]">
                    {/* Constructor Arguments */}
                    {contract.constructorArgs && (
                      <div>
                        <p className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-400" />
                          Constructor Arguments (ABI-encoded)
                        </p>
                        <div className="p-3 bg-[#1a2d4a] rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <code className="text-xs text-green-400 break-all">{contract.constructorArgs}</code>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyToClipboard(contract.constructorArgs, 'Constructor Args')}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Decoded Parameters */}
                        {contract.constructorParams && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-gray-500">Decoded parameters:</p>
                            {contract.constructorParams.map((param, i) => (
                              <div key={i} className="text-xs text-gray-400 font-mono pl-2">
                                {param.name}: <span className="text-blue-400">{param.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {!contract.constructorArgs && (
                      <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
                        <p className="text-sm text-green-400 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          No constructor arguments required
                        </p>
                      </div>
                    )}

                    {/* Source File Info */}
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Source File</p>
                      <div className="p-3 bg-[#1a2d4a] rounded-lg">
                        <p className="text-white font-mono text-sm">{contract.sourceFile}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Located at: /app/backend/contracts/mainnet/{contract.sourceFile}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Help Note */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div>
                <p className="text-white font-medium mb-1">Need Help?</p>
                <p className="text-sm text-gray-400">
                  If verification fails, ensure you're using the exact compiler settings. 
                  The contracts were compiled with Solidity 0.8.20, no optimization, and MIT license.
                  If you need the full source code, it's available in the backend contracts folder.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContractVerification;
